import { formatPrice } from '../utils/formatPrice';
import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { ChevronLeft, ChevronRight, X, Check, XCircle, FileText, Send } from 'lucide-react';
import { adminApi } from '../api/admin';
import AdminLayout from '../components/AdminLayout';

export default function AdminQuotes() {
  usePageTitle("Administration — Gestion des devis — Lodiss");

  const [quotes, setQuotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedQuote, setSelectedQuote] = useState<any | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [negotiatedPrices, setNegotiatedPrices] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState<Set<number>>(new Set());
  const [resendLoading, setResendLoading] = useState<Set<number>>(new Set());
  const [rowFeedback, setRowFeedback] = useState<Record<number, { type: 'ok' | 'err'; msg: string }>>({});

  useEffect(() => { loadQuotes(); }, [page, statusFilter]);

  const loadQuotes = () => {
    setLoading(true);
    adminApi.getQuotes({ page, limit: 20, status: statusFilter || undefined })
      .then(res => { setQuotes(res.items); setTotal(res.total); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const handleStatusChange = async (status: string) => {
    if (!selectedQuote) return;
    setSaving(true);
    try {
      const items = selectedQuote.status === 'accepted' ? Object.entries(negotiatedPrices).map(([productId, price]) => ({
        product_id: parseInt(productId),
        negotiated_price: parseFloat(price) || 0,
      })) : undefined;
      await adminApi.updateQuote(selectedQuote.id, { status, admin_notes: adminNotes || undefined, items });
      setSelectedQuote(null);
      setNegotiatedPrices({});
      setAdminNotes('');
      loadQuotes();
    } catch (err) { console.error('Failed to update quote:', err); }
    finally { setSaving(false); }
  };

  const handleDownloadPdf = async (quoteId: number) => {
    setPdfLoading(p => new Set(p).add(quoteId));
    try {
      const { blob, filename } = await adminApi.downloadQuotePdf(quoteId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('PDF download error:', err);
      showFeedback(quoteId, 'err', 'Erreur PDF');
    } finally {
      setPdfLoading(p => { const next = new Set(p); next.delete(quoteId); return next; });
    }
  };

  const handleResendEmail = async (quoteId: number) => {
    if (!window.confirm(`Renvoyer l\'email pour le devis #${quoteId} ?`)) return;
    setResendLoading(p => new Set(p).add(quoteId));
    try {
      const res = await adminApi.resendQuoteEmail(quoteId);
      if (res.success) {
        showFeedback(quoteId, 'ok', `Renvoyé à ${res.sent_to}`);
      } else {
        showFeedback(quoteId, 'err', 'Échec de l\'envoi');
      }
    } catch (err) {
      console.error('Email resend error:', err);
      showFeedback(quoteId, 'err', 'Erreur réseau');
    } finally {
      setResendLoading(p => { const next = new Set(p); next.delete(quoteId); return next; });
    }
  };

  const showFeedback = (quoteId: number, type: 'ok' | 'err', msg: string) => {
    setRowFeedback(prev => ({ ...prev, [quoteId]: { type, msg } }));
    setTimeout(() => {
      setRowFeedback(prev => {
        const next = { ...prev };
        delete next[quoteId];
        return next;
      });
    }, 4000);
  };

  const openDetail = (quote: any) => {
    setSelectedQuote(quote);
    setAdminNotes(quote.admin_notes || '');
    const prices: Record<number, string> = {};
    (quote.items || []).forEach((item: any) => { prices[item.product_id] = item.negotiated_price?.toString() || ''; });
    setNegotiatedPrices(prices);
  };

  const getStatusColor = (status: string) => ({
    pending: 'bg-amber-50 text-amber-700',
    reviewed: 'bg-blue-50 text-blue-700',
    accepted: 'bg-emerald-50 text-emerald-700',
    rejected: 'bg-red-50 text-red-700',
  }[status] || 'bg-gray-50 text-gray-700');

  const totalPages = Math.ceil(total / 20);

  return (
    <AdminLayout>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Devis</h1>
        <div className="flex gap-4 mb-6">
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="px-4 py-2 border rounded-lg">
            <option value="">Tous statuts</option>
            <option value="pending">En attente</option>
            <option value="reviewed">En révision</option>
            <option value="accepted">Accepté</option>
            <option value="rejected">Rejeté</option>
          </select>
        </div>
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">N°</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Client</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Articles</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center"><div className="w-6 h-6 border-2 border-lodiss-red border-t-transparent rounded-full animate-spin mx-auto" /></td></tr>
              ) : quotes.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Aucun devis</td></tr>
              ) : (
                quotes.map(quote => (
                  <tr key={quote.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm">#{quote.id}</td>
                    <td className="px-4 py-3 text-sm">{quote.user?.full_name || '-'}</td>
                    <td className="px-4 py-3 text-sm">{quote.items?.length || 0}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(quote.status)}`}>{quote.status}</span></td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(quote.created_at).toLocaleDateString('fr-FR')}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        {rowFeedback[quote.id] && (
                          <span className={`text-xs font-medium mr-2 ${rowFeedback[quote.id].type === 'ok' ? 'text-emerald-600' : 'text-red-500'}`}>
                            {rowFeedback[quote.id].msg}
                          </span>
                        )}
                        <button
                          onClick={() => handleDownloadPdf(quote.id)}
                          disabled={pdfLoading.has(quote.id)}
                          title="Télécharger PDF"
                          className="text-gray-500 flex items-center gap-1 hover:text-lodiss-red disabled:opacity-50 transition-colors"
                        >
                          {pdfLoading.has(quote.id) ? (
                            <div className="w-4 h-4 border-2 border-lodiss-red border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <FileText size={16} />
                          )}
                        </button>
                        <button
                          onClick={() => handleResendEmail(quote.id)}
                          disabled={resendLoading.has(quote.id)}
                          title="Renvoyer l'email"
                          className="text-gray-500 hover:text-lodiss-red disabled:opacity-50 transition-colors"
                        >
                          {resendLoading.has(quote.id) ? (
                            <div className="w-4 h-4 border-2 border-lodiss-red border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Send size={16} />
                          )}
                        </button>
                        <button onClick={() => openDetail(quote)} className="text-sm font-medium text-lodiss-red hover:underline ml-1">Voir</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-gray-500">{(page - 1) * 20 + 1} - {Math.min(page * 20, total)} sur {total}</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 border rounded-lg disabled:opacity-50"><ChevronLeft size={18} /></button>
              <span className="text-sm">{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 border rounded-lg disabled:opacity-50"><ChevronRight size={18} /></button>
            </div>
          </div>
        )}
        {selectedQuote && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-bold">Devis #{selectedQuote.id}</h2>
                <button onClick={() => setSelectedQuote(null)} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <p className="text-xs text-gray-500 mb-2">Articles</p>
                  <div className="space-y-2">
                    {(selectedQuote.items || []).map((item: any, i: number) => (
                      <div key={i} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <p className="font-medium">Produit #{item.product_id}</p>
                          <p className="text-sm">Qté: {item.quantity}</p>
                        </div>
                        {selectedQuote.status !== 'accepted' ? null : (
                          <div className="flex items-center gap-2 mt-2">
                            <label className="text-sm">Prix négocié (TND):</label>
                            <input
                              type="number"
                              step="0.01"
                              value={negotiatedPrices[item.product_id] || ''}
                              onChange={e => setNegotiatedPrices({ ...negotiatedPrices, [item.product_id]: e.target.value })}
                              className="flex-1 px-3 py-1 border rounded text-sm"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Notes admin</p>
                  <textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} rows={3} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Notes internes..." />
                </div>
              </div>
              <div className="flex justify-between p-4 border-t">
                {selectedQuote.status === 'pending' && (
                  <div className="flex gap-2">
                    <button onClick={() => handleStatusChange('accepted')} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"><Check size={16} />Accepter</button>
                    <button onClick={() => handleStatusChange('rejected')} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"><XCircle size={16} />Rejeter</button>
                  </div>
                )}
                <button onClick={() => setSelectedQuote(null)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 ml-auto">Fermer</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
